import { useTranslation } from 'react-i18next';
import { CloseButtonProps, toast, ToastOptions } from 'react-toastify';
import { ErrorCircle, InfoCircle, LinearCross, Refresh, TickCircle, WarningCircle } from '../icons';

type ToastType = 'success' | 'info' | 'error' | 'warning';

const iconMap = {
  success: <TickCircle className="text-clr-green-400" />,
  info: <InfoCircle className="text-clr-general-primary" />,
  warning: <WarningCircle className="warning-icon text-clr-beige-200" />,
  error: <ErrorCircle />,
};

// react-toastify clones this element with the real close handler, so closeToast is absent on first render.
const ToastCloseButton = ({ closeToast }: Partial<CloseButtonProps>) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      className="Toastify__close-button"
      aria-label={t('common:bottomNavigation.close')}
      onClick={(event) => {
        event.stopPropagation();
        closeToast?.(true);
      }}
    >
      <LinearCross />
    </button>
  );
};

const dismissedByUser = (type: ToastType) => type === 'error' || type === 'warning';

export const showToast = (
  type: ToastType,
  message: string,
  options?: ToastOptions,
  className?: string,
) => {
  const id = `${type}-${message}`;
  const requiresUserDismissal = dismissedByUser(type);
  toast[type](message, {
    toastId: id,
    icon: iconMap[type],
    closeButton: <ToastCloseButton />,
    className: className || 'custom-toast',
    position: 'top-center',
    autoClose: requiresUserDismissal ? false : 3000,
    hideProgressBar: requiresUserDismissal,
    pauseOnFocusLoss: false,
    closeOnClick: true,
    theme: 'colored',
    ...options,
  });
};

const REFRESH_TOAST_ID = 'refresh-in-progress';

const RefreshingMessage = () => {
  const { t } = useTranslation();

  return <>{`${t('common:refreshing')}...`}</>;
};

export const showRefreshToast = () => {
  toast.info(<RefreshingMessage />, {
    toastId: REFRESH_TOAST_ID,
    icon: <Refresh className="refreshing-toast-icon text-clr-info-toast-text" />,
    className: 'custom-toast refreshing-toast',
    position: 'top-center',
    autoClose: false,
    closeButton: false,
    closeOnClick: false,
    draggable: false,
    hideProgressBar: true,
    pauseOnFocusLoss: false,
    role: 'status',
    theme: 'colored',
  });
};

export const dismissRefreshToast = () => {
  toast.dismiss(REFRESH_TOAST_ID);
};

export const MINIMUM_REFRESH_TOAST_MS = 400;

let pendingRefreshCount = 0;

const holdToastForMinimumDisplay = (shownAt: number) => {
  const remainingDisplayTime = Math.max(0, MINIMUM_REFRESH_TOAST_MS - (Date.now() - shownAt));

  return new Promise((resolve) => setTimeout(resolve, remainingDisplayTime));
};

export const withRefreshToast = async <T,>(refresh: () => T | Promise<T>) => {
  pendingRefreshCount += 1;
  showRefreshToast();
  const shownAt = Date.now();

  try {
    return await refresh();
  } finally {
    pendingRefreshCount -= 1;
    if (pendingRefreshCount === 0) {
      await holdToastForMinimumDisplay(shownAt);
      if (pendingRefreshCount === 0) {
        dismissRefreshToast();
      }
    }
  }
};
